<template>
  <div class="w-full">
    <!-- FILTERS -->
    <div class="flex items-center gap-2 overflow-x-scroll scrollbar-hidden">
      <t-button
        v-for="(filter, idx) in filterButtons"
        :key="`${filter.title}.${filter.type}.${idx}`"
        size="sm"
        customWidth
        class="w-min"
        :active="isFilterActive(filter)"
        @click.native="handleFilterClick(filter)"
      >
        <t-text v-if="isFilterActive(filter)" class="font-semibold whitespace-nowrap" branded>{{
          filter.title === null ? $t('components.filters.all') : filter.title
        }}</t-text>
        <t-text v-else class="font-semibold whitespace-nowrap" color="gray">{{
          filter.title === null ? $t('components.filters.all') : filter.title
        }}</t-text>
        <template v-if="isMultiSelect(filter) || isSingleSelect(filter)" v-slot:right-side>
          <span
            class="border-l-transparent border-r-transparent"
            :class="{
              'border-t-gray': !isFilterActive(filter)
            }"
            :style="isFilterActive(filter) ? {borderColor: $helpers.getCorrectColor(themeColorHEX)} : {}"
            style="margin-top: 2px; border-top: 6px solid; border-left: 6px solid; border-right: 6px solid"
          />
        </template>
      </t-button>
    </div>
    <!-- FILTERS -->

    <!-- CURTAIN -->
    <t-curtain ref="curtain" @close="handleCurtainClose()">
      <t-curtain-tab
        v-for="filter in filtersCurtain"
        :key="filter.filter"
        :historyMode="false"
        :fullscreen="isSort(filter) ? false : isRange(filter) ? false : true"
        :name="`${filter.filter.replace(/\//g, '')}.${filter.title.replace(/\//g, '')}.${filter.type.replace(/\//g, '')}`"
        :title="isRange(filter) ? $t('components.filters.priceRange', {currency: currency === 'lightning-icon' ? '' : `(${currency})`}) : ''"
      >
        <template v-slot:sub-title v-if="!isRange(filter) && !isSort(filter)">
          <dark-sm-input
            :placeholder="$t('components.filters.search')"
            :ref="`filtersInput_${filter.filter.replace(/\//g, '')}`"
            @keyup="searchFilters(filter)"
          >
            <template v-slot:left-side>
              <ph-magnifying-glass :size="24" />
            </template>
          </dark-sm-input>
        </template>
        <div v-if="!isRange(filter)">
          <t-main-item
            v-for="val in filter.values.filter(item => item.name !== '')"
            :key="val.id"
            @clickLink="handleCurtainFilter(filter, val)"
            :text="Array.isArray(val.name) ? val.name[1] : val.name"
            text-class="line-clamp-2 break-word"
            icon="ph-sort-ascending"
            :image="Array.isArray(val.name) ? val.name[0] : ''"
            :imageSize="Array.isArray(val.name) ? 48 : 0"
            :imageBranded="false"
            imageCircle
            :iconSize="filter.filter === 'sort' ? 24 : 0"
            iconRight="ph-check"
            :iconRightSize="
              selected.find(el => el.title === filter.title && el.type === filter.type && el.filter === filter.filter) &&
              isValueSelected(selected, filter, val)
                ? 24
                : 0
            "
            class="cursor-pointer"
          />
          <t-empty-state v-if="filter.values.length === 0" type="search" class="my-10" />
        </div>
        <div v-else>
          <range-slider
            :min="filter.values[0]"
            :max="filter.values[1]"
            :value="valueHasRange(filter) ? getRangeFromValue(filter) : filter.values"
            :key="rangeKey"
            @change="v => handleCurtainFilter(filter, v)"
            :color="themeColorHEX"
          />
        </div>
        <template v-slot:footer>
          <div class="flex items-center gap-4">
            <t-button @click.native="resetCurtainFilter(filter)">
              <t-text class="font-semibold">{{ $t('components.filters.reset') }}</t-text>
            </t-button>
            <t-button ref="btn" @click.native="$refs.curtain.hide()" :color="'branded'">
              <t-text class="font-semibold">{{ $t('components.filters.apply') }}</t-text>
            </t-button>
          </div>
        </template>
      </t-curtain-tab>
    </t-curtain>
    <!-- CURTAIN -->
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import {mapMutations} from 'vuex'
  import AXIOS from '@/integrations/axios'
  import RangeSlider from '@/components/RangeSlider'
  export default {
    name: 't-filters',
    data() {
      return {
        filters: [],
        currFilterIdx: null,
        backupFilter: [],
        filtersBeforeCurtainOpened: [],
        selected: this.value.length > 0 ? this.value : [this.defaultFilter],
        rangeKey: 0.1
      }
    },
    components: {
      RangeSlider
    },
    props: {
      defaultFilter: {
        type: Object,
        default: () => {
          return {
            filter: null,
            title: null,
            type: 'switch'
          }
        }
      },
      value: {
        type: Array,
        default: () => {
          return []
        }
      },
      filtersUrl: {
        type: String,
        required: false,
        default: ''
      }
    },
    computed: {
      ...mapGetters('user', ['userMenu', 'menuLoaded', 'userCurrentModule']),
      ...mapGetters(['themeColorHEX']),
      filtersCurtain() {
        return this.filters.filter(item => item.type !== 'switch' && item.filter !== null)
      },
      filterButtons() {
        return this.filters.filter(item => item.filter !== 'sort')
      },
      hasSort() {
        return !!this.filters.filter(item => item.filter === 'sort')
      },
      sortFilter() {
        return this.filters.filter(item => item.filter === 'sort')
      },
      sortApplied() {
        return !!this.selected.find(item => item.filter === 'sort')
      },
      currency() {
        return decodeURIComponent(this.$cookies.get('currency_symbol'))
      }
    },
    async mounted() {
      await this.menuLoaded

      const [filters] = await Promise.all([this.getFilters()])

      if (filters) {
        this.filters = [this.defaultFilter, ...filters.items]
        this.$nextTick(() => {
          this.setHeaderLoadedState(true)
          if (filters.items.filter(el => el.filter !== null && el.filter !== 'sort' && el.title !== 'All').length > 0)
            this.$emit('loaded')
        })
      }
    },
    methods: {
      ...mapMutations('layout', ['setHeaderLoadedState']),
      getFilters() {
        return AXIOS.get(
          this.filtersUrl === ''
            ? `/${this.userCurrentModule.type}?module=${this.userCurrentModule.app_module_id}&filters-list`
            : this.filtersUrl
        ).then(res => {
          return (
            res && {
              items: res.data || []
            }
          )
        })
      },
      async reloadFilters() {
        const [filters] = await Promise.all([this.getFilters()])

        if (filters) {
          this.filters = [this.defaultFilter, ...filters.items]
        }
      },
      searchFilters(filter, customQuery = false) {
        const query = customQuery
          ? ''
          : String(this.$refs[`filtersInput_${filter.filter.replace(/\//g, '')}`][0].getValue()).toLowerCase()
        this.currFilterIdx = this.filters.indexOf(filter)
        if (this.backupFilter.length === 0) this.backupFilter = this.filters[this.currFilterIdx].values
        if (query.length === 0) {
          this.filters[this.currFilterIdx].values = this.backupFilter
          this.backupFilter = []
          return
        }
        this.filters[this.currFilterIdx].values = this.backupFilter.filter(item => {
          const lowercasedItem = String(item.name).toLowerCase()
          return lowercasedItem.includes(query)
        })
      },
      isValueSelected(selected, filter, val) {
        const VALUES = selected.find(el => el.title === filter.title && el.type === filter.type && el.filter === filter.filter).values
        let valid = false
        VALUES.forEach(obj => {
          if (obj.id == val.id && obj.name == val.name) {
            valid = true
          }
        })
        return valid
      },
      getFiltersString() {
        if (this.selected.find(el => el.filter === null) && !this.selected.find(el => el.filter === 'sort')) return ''
        const string = ['filter=']
        this.selected.forEach(item => {
          if (item.values && item.values.length !== 0) {
            string.push(`${item.filter}:`)
            let vals = []

            if (item.filter === 'price') {
              vals = item.values
            } else {
              item.values.forEach(val => {
                if (val.id !== null) vals.push(val.id)
              })
            }

            string.push(`${vals.join(',')};`)
          }
        })

        return string.join('')
      },
      openSortCurtain() {
        if (!this.hasSort) return
        this.$refs.curtain.tab(`default/${this.sortFilter[0].filter}.${this.sortFilter[0].title}.${this.sortFilter[0].type}`)
      },
      handleFilterClick(filter) {
        if (this.isDefaultFilter(filter) || this.isRadio(filter)) {
          if (!this.selected.includes(filter)) {
            const newFilter = this.selected.filter(filter => filter.filter === 'sort')
            newFilter.push(filter)
            this.selected = newFilter
          }
          this.$emit('filtersApplied', {
            str: this.getFiltersString(),
            raw: this.selected,
            sort: !!this.selected.find(filter => filter.filter === 'sort')
          })
          return
        }
        if (this.isSwitch(filter)) {
          if (this.selected.includes(this.defaultFilter)) {
            const idx = this.selected.indexOf(this.defaultFilter)
            this.selected.splice(idx, 1)
          }
          if (this.selected.includes(filter)) {
            const idx = this.selected.indexOf(filter)
            this.selected.splice(idx, 1)
          } else {
            this.selected.push(filter)
          }

          if (this.selected.length === 0) {
            const newFilter = this.selected.filter(filter => filter.filter === 'sort')
            newFilter.push(this.defaultFilter)
            this.selected = newFilter
          }

          this.$emit('filtersApplied', {
            str: this.getFiltersString(),
            raw: this.selected,
            sort: !!this.selected.find(filter => filter.filter === 'sort')
          })
          return
        }
        if (this.isMultiSelect(filter) || this.isRange(filter) || this.isSingleSelect(filter)) {
          this.filtersBeforeCurtainOpened = JSON.parse(JSON.stringify(this.selected))
          this.$refs.curtain.tab(
            `default/${filter.filter.replace(/\//g, '')}.${filter.title.replace(/\//g, '')}.${filter.type.replace(/\//g, '')}`
          )
          return
        }
      },
      valueHasRange(filter) {
        let hasRange = false
        this.value.forEach(_filter => {
          if (_filter.type === filter.type && _filter.title === filter.title && _filter.filter === filter.filter) hasRange = true
        })
        return hasRange
      },
      getRangeFromValue(filter) {
        let range = null
        this.value.forEach(_filter => {
          if (_filter.type === filter.type && _filter.title === filter.title && _filter.filter === filter.filter) range = _filter
        })
        return range.values
      },
      handleCurtainFilter(filter, value) {
        const selectedFilter = this.selected.find(
          el => el.title === filter.title && el.type === filter.type && el.filter === filter.filter
        )
        const indexOfSelected = this.selected.indexOf(selectedFilter)

        if (this.selected.includes(this.defaultFilter) && filter.filter !== 'sort') {
          const idx = this.selected.indexOf(this.defaultFilter)
          this.selected.splice(idx, 1)
        }

        if (this.isMultiSelect(filter)) {
          if (selectedFilter) {
            if (selectedFilter.values.includes(value)) {
              if (selectedFilter.values.length === 1) this.selected.splice(indexOfSelected, 1)
              selectedFilter.values.splice(selectedFilter.values.indexOf(value), 1)
            } else selectedFilter.values.push(value)
          } else {
            this.selected.push({
              filter: filter.filter,
              title: filter.title,
              type: filter.type,
              values: [value]
            })
          }
        }

        if (this.isSingleSelect(filter)) {
          if (selectedFilter) {
            if (selectedFilter.values.includes(value)) {
              selectedFilter.values.splice(selectedFilter.values.indexOf(value), 1)
            } else {
              selectedFilter.values = [value]
            }
            if (selectedFilter.values.length === 0) this.selected.splice(indexOfSelected, 1)
          } else {
            this.selected.push({
              filter: filter.filter,
              title: filter.title,
              type: filter.type,
              values: [value]
            })
          }
        }

        if (this.isRange(filter)) {
          if (selectedFilter) {
            selectedFilter.values = value
          } else {
            this.selected.push({
              filter: filter.filter,
              title: filter.title,
              type: filter.type,
              values: value
            })
          }
        }

        if (this.selected.length === 0) {
          const newFilter = this.selected.filter(filter => filter.filter === 'sort')
          newFilter.push(this.defaultFilter)
          this.selected = newFilter
        }
      },
      handleCurtainClose() {
        if (JSON.stringify(this.filtersBeforeCurtainOpened) !== JSON.stringify(this.selected)) {
          this.$emit('filtersApplied', {
            str: this.getFiltersString(),
            raw: this.selected,
            sort: !!this.selected.find(el => el.filter === 'sort')
          })
        }
        this.filtersBeforeCurtainOpened = []
        if (this.currFilterIdx) {
          this.$refs[`filtersInput_${this.filters[this.currFilterIdx].filter.replace(/\//g, '')}`][0].inputVal = ''
          this.searchFilters(this.filters[this.currFilterIdx], true)
        }
      },
      resetCurtainFilter(filter) {
        const selectedFilter = this.selected.find(
          el => el.title === filter.title && el.type === filter.type && el.filter === filter.filter
        )
        const indexOfSelected = this.selected.indexOf(selectedFilter)
        this.rangeKey++
        if (selectedFilter) {
          this.selected.splice(indexOfSelected, 1)
          if (this.selected.length === 0) this.selected = [this.defaultFilter]
          this.$refs.curtain.hide()
        } else this.$refs.curtain.hide()
      },
      isMultiSelect(filter) {
        return filter.type === 'multiselect'
      },
      isSingleSelect(filter) {
        return filter.type === 'select'
      },
      isSwitch(filter) {
        return filter.type === 'switch'
      },
      isRadio(filter) {
        return filter.type === 'radio'
      },
      isRange(filter) {
        return filter.type === 'slider'
      },
      isSort(filter) {
        return filter.filter === 'sort'
      },
      isDefaultFilter(filter) {
        return filter.filter === this.defaultFilter.filter
      },
      isFilterActive(filter) {
        return filter.values
          ? this.selected.find(
              el =>
                el.title?.replace(/\s?\(.*?\)\s?/g, '') === filter.title?.replace(/\s?\(.*?\)\s?/g, '') &&
                el.type === filter.type &&
                el.filter === filter.filter
            ) &&
              this.selected.find(
                el =>
                  el.title?.replace(/\s?\(.*?\)\s?/g, '') === filter.title?.replace(/\s?\(.*?\)\s?/g, '') &&
                  el.type === filter.type &&
                  el.filter === filter.filter
              ).values.length > 0
          : !!this.selected.find(
              el =>
                el.title?.replace(/\s?\(.*?\)\s?/g, '') === filter.title?.replace(/\s?\(.*?\)\s?/g, '') &&
                el.type === filter.type &&
                el.filter === filter.filter
            )
      }
    }
  }
</script>
